import type { CreateBoardReq } from '@/api/gen/_model/createBoardReq';

import type { BoardTemplate, BoardTemplateKey } from '../types/template';

/**
 * 공지 그룹 이모지. 이관 스크립트
 * (20260828_board_notice_to_pin_migration_jihyun.js)와 **같은 값**이어야 한다.
 *
 * 이름은 반대로 일부러 다르다 — 이관은 8,489개 보드의 로케일을 알 방법이 없어 `📢` 를
 * 쓰지만, 여기는 생성 시점 언어를 알므로 그 언어로 쓴다(설계 문서 §3.5 의 "언어를 모를 때"
 * 조건이 이 경로에는 성립하지 않는다).
 */
const PIN_GROUP_EMOJI = '📌';

/**
 * 템플릿의 i18n 키를 현재 언어 평문으로 해석해 보드 생성 payload 로 변환한다.
 * 생성된 title/안내문/group.name 은 백엔드에 그대로 저장되므로 생성 시점 언어로 고정된다.
 *
 * @param pinGroupName 안내문을 담을 공지 그룹의 이름. 호출부가 `t('board.notice.title')`
 *   로 해석해 넘긴다. 이 유틸이 직접 번역하지 않는 이유: 인자로 받는 t 는 `boardTemplates`
 *   네임스페이스 전용이라 `board` 키를 해석할 수 없다. 두 번째 t 를 받는 것보다
 *   **이미 해석된 문자열 하나**를 받는 편이 호출부에서 무엇이 저장되는지 보인다.
 */
export function templateToCreateBoardReq(
  template: BoardTemplate,
  t: (key: BoardTemplateKey) => string,
  pinGroupName: string,
): CreateBoardReq {
  const noticeKey = template.noticeKey;

  // 안내문이 있고 그룹 보드면, 안내문을 담을 공지 그룹을 맨 앞에 끼운다.
  // 그룹 보드는 컬럼만 렌더하고 groupId: null 인 글을 표시할 자리가 없기 때문이다.
  // 그룹이 없는 담벼락은 담벼락 lane 에 그대로 고정하면 되므로 그룹을 만들지 않는다
  // (그룹을 만드는 순간 boardType 이 WALL → GROUP 으로 바뀌어 레이아웃이 달라진다).
  const needsPinGroup = Boolean(noticeKey) && template.groups.length > 0;

  return {
    title: t(template.nameKey),
    emoji: template.emoji,
    // 배경은 생성 시 한 번만 심는다 — 이후 보드 설정에서 자유롭게 바꿀 수 있다.
    background: template.background,
    groups: [
      ...(needsPinGroup
        ? [{ name: pinGroupName, emoji: PIN_GROUP_EMOJI }]
        : []),
      ...template.groups.map(group => ({
        name: t(group.nameKey),
        emoji: group.emoji,
      })),
    ],
    posts: noticeKey
      ? [
          {
            content: t(noticeKey),
            groupIndex: needsPinGroup ? 0 : undefined,
            pinned: true,
          },
        ]
      : [],
  };
}
